// The company record's deals in motion (pipeline P5 step 6; DEALS-LIVE-CARD-BRIEF-2026-10-04.md section 5 item 8,
// drawn in a-company.html; U240, Mike's "Lines that open the card"). Mike, 4 Oct 2026: "This then has to link back to
// the company record so that the information is mirrored."
//
// The same read as Deals (GET /api/deals/heat), and the same line and card (LiveCard.tsx): one line per open
// opportunity, keenest first, each opening in place into the card, which says "See it on Deals" where Deals says "Open
// the company" (ctx.place). An edit on either page is on the other at its next read. The record's company is the
// opportunity's own company, or its account when the opportunities are its brands', as the Deals line counts them.
import { ActionButton } from '../parts/index.js';
import { useEffect, useState } from 'react';
import { useLoad } from '../records/shared.js';
import { dealsApi, type Heat, type HeatRow } from './api.js';
import { DealCard, liveContextOf, OpportunityLineView, useCardNotes, type LiveContext } from './LiveCard.js';
import './deals.css';

export type CompanyDealsRead =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'ready'; rows: HeatRow[]; ctx: LiveContext; accountId: string | null; heat: Heat };

/** This company's open opportunities in the heat read, keenest first, those with no keenness last. */
export function companyRows(heat: Heat, companyId: string): HeatRow[] {
  const later = (a: string | null, b: string | null) => (b ?? '').localeCompare(a ?? '');
  return heat.lines
    .filter((r) => r.organisationId === companyId || r.accountId === companyId)
    .sort((a, b) => (a.keenness === null ? 1 : 0) - (b.keenness === null ? 1 : 0) || (b.keenness ?? 0) - (a.keenness ?? 0)
      || later(a.lastHeardAt, b.lastHeardAt) || (a.product ?? a.title).localeCompare(b.product ?? b.title) || a.id.localeCompare(b.id));
}

/**
 * The heat read for one company's record, read again quietly after a write on its cards, as Deals is; the cards' notes
 * are kept here, and a line a write moved (a keenness re-sorts the list) is kept in view.
 */
export function useCompanyDeals(store: string, companyId: string, open: (path: string) => void, recordChanged?: () => void): CompanyDealsRead {
  const [loaded] = useLoad(() => dealsApi.heat(store), [store]);
  const [fresh, setFresh] = useState<{ store: string; data: Heat } | null>(null);
  const kept = useCardNotes();
  const [follow, setFollow] = useState<string | null>(null);
  const refresh = (after?: { accountId: string; dealId: string }) => {
    recordChanged?.();
    void dealsApi.heat(store).then((data) => { setFresh({ store, data }); if (after) setFollow(after.dealId); }).catch(() => undefined);
  };
  useEffect(() => {
    if (!follow) return;
    document.getElementById(`rec-a-${follow}`)?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
    setFollow(null);
  }, [follow]);
  if (loaded.kind === 'loading') return { kind: 'loading' };
  if (loaded.kind === 'error') return { kind: 'error' };
  const heat = fresh && fresh.store === store ? fresh.data : loaded.data;
  const rows = companyRows(heat, companyId);
  return { kind: 'ready', rows, ctx: liveContextOf(heat, store, 'company', open, refresh, kept), accountId: rows[0]?.accountId ?? null, heat };
}

/**
 * "In motion" on the record (a-company.html): the head with the count, "by keenness" and "See them on Deals"; the
 * column heads; a line per opportunity, the keenest opened as the drawing opens it, each opening in place into its card.
 */
export function InMotion({ rows, ctx, accountId, companyName, title = 'In motion', meta = 'by keenness', openFirst = true }: {
  rows: HeatRow[]; ctx: LiveContext; accountId: string | null; companyName: string;
  /** The record's group (pipeline RC4): "Open", or "Open, on <company>'s record" for the deal the money depends on. */
  title?: string; meta?: string; openFirst?: boolean;
}) {
  const [opened, setOpened] = useState<ReadonlySet<string>>(() => new Set(openFirst && rows[0] ? [rows[0].id] : []));
  // A line is open while pressed open or while its card says something; closing it ends both.
  const isOpen = (id: string) => opened.has(id) || Boolean(ctx.notes[id]);
  const toggle = (id: string) => {
    const closing = isOpen(id);
    if (closing) ctx.setNote(id, null);
    setOpened((s) => { const n = new Set(s); if (closing) n.delete(id); else n.add(id); return n; });
  };
  return (
    <div className="lv-rec">
      <div className="hx-bandline">
        <h3>{title}</h3><span className="hx-cnt">{rows.length}</span><span className="hx-meta">{meta}</span>
        {accountId ? <ActionButton reading type="button" className="hx-link" onClick={() => ctx.open(`/deals?open=${encodeURIComponent(accountId)}`)}>See them on Deals</ActionButton> : null}
      </div>
      <div className="lv-colhead"><span /><span>Opportunity and next step</span><span>Stage, and what is new today</span><span className="r">Revenue potential</span><span /></div>
      {rows.map((r) => {
        const shown = isOpen(r.id);
        const body = `rec-b-${r.id}`;
        return (
          <div className={shown ? 'lv-acard open' : 'lv-acard'} key={r.id} id={`rec-a-${r.id}`}>
            <OpportunityLineView row={r} ctx={ctx} isOpen={shown} toggle={() => toggle(r.id)} bodyId={body} />
            {shown ? <div className="lv-abody" id={body}><DealCard row={r} accountId={r.accountId} accountName={companyName} ctx={ctx} /></div> : null}
          </div>
        );
      })}
    </div>
  );
}
